import { useMpxAgGridState } from "@platform/hooks";
import { useCallback, useMemo } from "react";

import { QUOTES_GRID_ID } from "../utils/quotesGridStyles";
import {
  omitColumnVisibility,
  sanitizeQuotesGridState,
} from "../utils/sanitizeQuotesGridState";

import { useFrozenOnKey } from "./useFrozenOnKey";

// ---------------------------------------------------------------------------
// Plan-editor grid-state persistence — extracted to keep Quotes below the
// complexity limit
// ---------------------------------------------------------------------------

export const useQuotesGridStatePersistence = (
  ecoRfpId: string,
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types -- inferred; extracted interface would be large
) => {
  const { gridProps: quotesGridStateProps } = useMpxAgGridState({
    scopeType: "rfp",
    scopeId: ecoRfpId,
    gridId: QUOTES_GRID_ID,
    enabled: !!ecoRfpId,
  });

  // Carrier column order is managed by useCarrierSequence — skip saving
  // grid state for pure column-move events to prevent constant PUTs during drag.
  const baseOnStateUpdated = quotesGridStateProps.onStateUpdated;
  const onQuotesStateUpdated = useCallback(
    (event: Parameters<typeof baseOnStateUpdated>[0]) => {
      if (event.sources?.every((s) => s === "columnOrder" || s === "scroll"))
        return;
      baseOnStateUpdated(event);
    },
    [baseOnStateUpdated],
  );
  // Freeze initialState per key so PUT 200 cache updates don't re-apply server
  // state and clobber in-session column resizes.
  const sanitizedInitialState = useMemo(
    () =>
      sanitizeQuotesGridState(
        omitColumnVisibility(quotesGridStateProps.initialState),
      ),
    [quotesGridStateProps.initialState],
  );
  const frozenInitialState = useFrozenOnKey(
    sanitizedInitialState,
    quotesGridStateProps.key,
  );

  return useMemo(
    () => ({
      key: quotesGridStateProps.key,
      initialState: frozenInitialState,
      onStateUpdated: onQuotesStateUpdated,
    }),
    [quotesGridStateProps.key, frozenInitialState, onQuotesStateUpdated],
  );
};
